import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import './PageCorpo.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─── Helpers ─────────────────────────────────

const JOURS = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
const MOIS  = ['janvier','février','mars','avril','mai','juin','juillet','août',
               'septembre','octobre','novembre','décembre'];

function fmtDate(d) {
  if (!d) return '—';
  const date = new Date(d + 'T12:00:00');
  return `${JOURS[date.getDay()]} ${date.getDate()} ${MOIS[date.getMonth()]} ${date.getFullYear()}`;
}

function fmt$(v) {
  if (v == null) return '0,00 $';
  return parseFloat(v).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' });
}

// ─── Statut paiement ─────────────────────────

function StatutPaiement({ paye, date, label, montant }) {
  if (paye) {
    return (
      <div className="corpo-paiement-ok">
        <Icon name="succes" size={16} weight="fill" />
        <span>{label} reçu{date ? ` le ${date.slice(0, 10)}` : ''}</span>
      </div>
    );
  }
  return (
    <div className="corpo-paiement-attente">
      <Icon name="heure" size={15} weight="regular" color="var(--muted)" />
      <span>{label} — En attente</span>
      <strong>{fmt$(montant)}</strong>
    </div>
  );
}

// ─── Page principale ─────────────────────────

export default function PageCorpo() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  const [data, setData]       = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]   = useState('');
  const [payInProgress, setPayInProgress] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const paid  = searchParams.get('paid');
  const confirmed = searchParams.get('confirmed') === '1';

  useEffect(() => {
    async function charger() {
      setChargement(true);
      try {
        const res = await fetch(`${API_URL}/api/public/corpo/${token}`);
        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || 'Page introuvable');
        }
        const json = await res.json();
        setData(json);
      } catch (err) {
        setErreur(err.message);
      } finally {
        setChargement(false);
      }
    }
    charger();
  }, [token]);

  // Afficher bandeau de confirmation pendant 6 secondes
  useEffect(() => {
    if (confirmed && paid) {
      setShowConfirmation(true);
      const t = setTimeout(() => setShowConfirmation(false), 6000);
      return () => clearTimeout(t);
    }
  }, [confirmed, paid]);

  async function payer(typePaiement) {
    setPayInProgress(true);
    try {
      const res = await fetch(`${API_URL}/api/stripe/corpo-checkout`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ token, type_paiement: typePaiement }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Erreur paiement');
      window.location.href = json.url;
    } catch (err) {
      alert(err.message);
      setPayInProgress(false);
    }
  }

  // ─── États de chargement ──────────────────────

  if (chargement) {
    return (
      <div className="corpo-page corpo-chargement">
        <div className="corpo-logo">ESCAMEL</div>
        <div className="corpo-spinner" />
        <div className="muted">Chargement…</div>
      </div>
    );
  }

  if (erreur || !data) {
    return (
      <div className="corpo-page corpo-chargement">
        <div className="corpo-logo">ESCAMEL</div>
        <div className="corpo-erreur">{erreur || 'Page introuvable.'}</div>
        <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
          Vérifiez que le lien est complet et exact.
        </div>
      </div>
    );
  }

  const { evenement, client, plateaux, montant_total,
          acompte_pct, acompte_montant, solde_montant,
          acompte_paye, acompte_date, solde_paye, solde_date,
          type_paiement } = data;

  const toutPaye = acompte_paye && solde_paye;
  const sansAcompte = acompte_pct === 0;

  return (
    <div className="corpo-page">

      {/* Bandeau confirmation paiement */}
      {showConfirmation && (
        <div className="corpo-bandeau-ok">
          <Icon name="succes" size={16} weight="fill" />
          Paiement reçu — Merci !
        </div>
      )}

      {/* En-tête */}
      <div className="corpo-header">
        <div className="corpo-logo">ESCAMEL</div>
        <div className="corpo-logo-sub">Artisans du Fumoir</div>
      </div>

      <div className="corpo-container">

        {/* Titre */}
        <div className="corpo-titre-section">
          <div className="corpo-entreprise-label">Événement corporatif pour :</div>
          <h1 className="corpo-entreprise">{client?.entreprise || 'Votre entreprise'}</h1>
          <div className="corpo-ev-nom">{evenement.nom}</div>
        </div>

        {/* Infos événement */}
        <div className="corpo-card">
          <div className="corpo-infos-grid">
            <div className="corpo-info-item">
              <span className="corpo-info-icone"><Icon name="date" size={15} /></span>
              <span>{fmtDate(evenement.date_event)}</span>
            </div>
            <div className="corpo-info-item">
              <span className="corpo-info-icone"><Icon name="heure" size={15} /></span>
              <span>{evenement.heure_debut} — {evenement.heure_fin}</span>
            </div>
            <div className="corpo-info-item">
              <span className="corpo-info-icone"><Icon name="lieu" size={15} /></span>
              <span>
                {evenement.lieu}
                {evenement.adresse ? `, ${evenement.adresse}` : ''}
                {evenement.ville ? `, ${evenement.ville}` : ''}
              </span>
            </div>
            {evenement.google_maps_url && (
              <div className="corpo-info-item">
                <span className="corpo-info-icone"><Icon name="lieu" size={15} /></span>
                <a href={evenement.google_maps_url} target="_blank" rel="noopener noreferrer" className="corpo-lien">
                  Voir sur la carte
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Menu / Plateaux */}
        {plateaux && plateaux.length > 0 && (
          <div className="corpo-section-titre">Votre menu</div>
        )}
        {plateaux.map(p => (
          <div key={p.id} className="corpo-card corpo-plateau-card">
            <div className="corpo-plateau-header">
              <span className="corpo-info-icone"><Icon name="repas" size={16} /></span>
              <span className="corpo-plateau-nom">
                {p.label}
                {p.nb_personnes > 1 && (
                  <span className="corpo-plateau-pers"> ({p.nb_personnes} personnes)</span>
                )}
              </span>
              <span className="corpo-plateau-prix gold">{fmt$(p.prix)}</span>
            </div>

            {/* Contenu détaillé */}
            {Array.isArray(p.contenu) && p.contenu.length > 0 && (
              <ul className="corpo-plateau-contenu">
                {p.contenu.map((c, i) => (
                  <li key={i}>
                    {c.label || `${c.quantite} ${c.unite} ${c.produit}`}
                  </li>
                ))}
              </ul>
            )}

            {/* Sides */}
            {Array.isArray(p.sides_inclus) && p.sides_inclus.length > 0 && (
              <div className="corpo-sides">
                <strong>Accompagnements inclus :</strong>
                <span className="corpo-muted"> {p.sides_inclus.join(' · ')}</span>
              </div>
            )}
          </div>
        ))}

        {/* Facture */}
        <div className="corpo-section-titre">Votre facture</div>
        <div className="corpo-card corpo-facture">
          <table className="corpo-table">
            <tbody>
              {plateaux.map(p => (
                <tr key={p.id}>
                  <td className="corpo-table-lbl">{p.label}{p.nb_personnes > 1 ? ` (${p.nb_personnes} pers.)` : ''}</td>
                  <td className="corpo-table-val gold">{fmt$(p.prix)}</td>
                </tr>
              ))}
              <tr className="corpo-table-total">
                <td>TOTAL</td>
                <td className="gold">{fmt$(montant_total)}</td>
              </tr>
            </tbody>
          </table>

          {!sansAcompte && (
            <div className="corpo-paiements">
              <div className="corpo-paiement-ligne">
                <span className="corpo-muted">Acompte ({acompte_pct}%)</span>
                <span>{fmt$(acompte_montant)}</span>
              </div>
              <div className="corpo-paiement-ligne">
                <span className="corpo-muted">Solde à la livraison</span>
                <span>{fmt$(solde_montant)}</span>
              </div>
            </div>
          )}

          {/* Statuts paiement */}
          <div className="corpo-statuts-paiement">
            {sansAcompte ? (
              <>
                <StatutPaiement
                  paye={solde_paye}
                  date={solde_date}
                  label="Paiement total"
                  montant={montant_total}
                />
              </>
            ) : (
              <>
                <StatutPaiement
                  paye={acompte_paye}
                  date={acompte_date}
                  label={`Acompte (${acompte_pct}%)`}
                  montant={acompte_montant}
                />
                <StatutPaiement
                  paye={solde_paye}
                  date={solde_date}
                  label="Solde"
                  montant={solde_montant}
                />
              </>
            )}

            {toutPaye && (
              <div className="corpo-tout-paye">
                <Icon name="succes" size={18} weight="fill" />
                Paiement complet — Merci !
              </div>
            )}
          </div>

          {/* Boutons de paiement */}
          {type_paiement !== 'individuel' && (
            <div className="corpo-boutons-paiement">
              {!sansAcompte && !acompte_paye && (
                <button
                  className="corpo-btn-payer"
                  onClick={() => payer('acompte')}
                  disabled={payInProgress}
                >
                  {payInProgress ? 'Redirection…' : (
                <><Icon name="argent" size={15} weight="bold" /> Payer l'acompte {fmt$(acompte_montant)}</>
              )}
                </button>
              )}
              {(!sansAcompte ? (acompte_paye && !solde_paye) : !solde_paye) && (
                <button
                  className="corpo-btn-payer"
                  onClick={() => payer('solde')}
                  disabled={payInProgress}
                >
                  {payInProgress ? 'Redirection…' : (
                <><Icon name="argent" size={15} weight="bold" /> Payer le solde {fmt$(sansAcompte ? montant_total : solde_montant)}</>
              )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Notes du client */}
        {evenement.notes_client && (
          <>
            <div className="corpo-section-titre">Notes</div>
            <div className="corpo-card">
              <p className="corpo-notes">{evenement.notes_client}</p>
            </div>
          </>
        )}

        {/* Contact */}
        <div className="corpo-footer">
          Des questions ?&nbsp;
          <a href="mailto:commandes@escamel.ca" className="corpo-lien">
            commandes@escamel.ca
          </a>
        </div>

      </div>
    </div>
  );
}
